import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lightning } from "@phosphor-icons/react";
import { fetcher, fmtN, todayISO } from "./common";

const KINDS = [
  { id: "banque_depense", label: "Banque — Dépense" },
  { id: "banque_recette", label: "Banque — Recette" },
  { id: "caisse_depense", label: "Caisse — Dépense" },
  { id: "caisse_recette", label: "Caisse — Recette" },
  { id: "note_frais", label: "Note de frais" },
];

/** Saisie guidée une ligne (montant TTC + compte) avec TVA automatique — logique macompta, plan SYSCOHADA. */
export default function QuickEntry({ file }) {
  const [kind, setKind] = React.useState("banque_depense");
  const [form, setForm] = React.useState({ date: todayISO(), label: "", amount: "", account_code: "", tiers: "" });
  const [tva, setTva] = React.useState(true);
  const [beneficiary, setBeneficiary] = React.useState("personnel");
  const [saving, setSaving] = React.useState(false);
  const { data: accounts = [] } = useSWR(`/accounting/files/${file.id}/accounts`, fetcher);

  if (file.chart_type !== "syscohada") {
    return (
      <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center text-sm text-zinc-500" data-testid="quick-unavailable">
        La saisie simplifiée (Banque / Caisse / Notes de frais avec TVA automatique) est disponible pour les dossiers en plan SYSCOHADA. Utilisez l'onglet « Débit / Crédit » pour ce dossier.
      </div>
    );
  }

  const amount = parseFloat(form.amount) || 0;
  const rate = tva ? (file.tva_rate || 0) : 0;
  const ht = rate ? amount / (1 + rate / 100) : amount;
  const tvaAmt = amount - ht;
  const isNoteFrais = kind === "note_frais";
  const tiersLabel = kind.includes("recette") ? "Client" : isNoteFrais ? "Bénéficiaire" : "Fournisseur";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/accounting/files/${file.id}/quick-entry`, {
        kind, date: form.date, label: form.label, amount_ttc: amount,
        account_code: form.account_code.trim(), tva_applicable: tva,
        tiers: form.tiers || null, beneficiary, status: "valide",
      });
      toast.success(`Écriture ${r.data.piece_no} enregistrée`);
      setForm({ ...form, label: "", amount: "", tiers: "" });
      mutate(`/accounting/files/${file.id}/entries`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
      <form onSubmit={submit} className="xl:col-span-3 border border-zinc-200 bg-white rounded-md p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Saisie simplifiée</div>
        <div className="flex flex-wrap gap-2" data-testid="quick-kinds">
          {KINDS.map((k) => (
            <button type="button" key={k.id} onClick={() => setKind(k.id)} data-testid={`quick-kind-${k.id}`}
              className={`px-3 py-1.5 rounded-full text-xs border ${kind === k.id ? "bg-[#2E3192] text-white border-[#2E3192]" : "border-zinc-200 text-zinc-700 hover:bg-zinc-100"}`}>
              {k.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="quick-date" /></div>
          <div><Label>{tiersLabel}</Label><Input value={form.tiers} onChange={(e) => setForm({ ...form, tiers: e.target.value })} data-testid="quick-tiers" placeholder="Nom du tiers (optionnel)" /></div>
        </div>
        <div><Label>Libellé</Label><Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="quick-label" placeholder="Paiement loyer juin…" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{kind.includes("recette") ? "Compte de produit" : "Compte de charge"}</Label>
            <Input required list="quick-acc-codes" value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} data-testid="quick-account" placeholder="601" />
            <datalist id="quick-acc-codes">{accounts.map((a) => (<option key={a.code} value={a.code}>{a.name}</option>))}</datalist>
          </div>
          <div><Label>Montant TTC ({file.currency})</Label><Input required type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="quick-amount" /></div>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={tva} onCheckedChange={(v) => setTva(!!v)} data-testid="quick-tva" />
            <span>TVA {file.tva_rate}% applicable</span>
          </label>
          {isNoteFrais && (
            <Select value={beneficiary} onValueChange={setBeneficiary}>
              <SelectTrigger className="w-52 h-8" data-testid="quick-beneficiary"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="personnel">Personnel (4212)</SelectItem>
                <SelectItem value="dirigeant">Dirigeant / associé (4621)</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <Button type="submit" disabled={saving || !amount || !form.account_code || !form.label} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="quick-submit">
          <Lightning size={16} className="mr-1" /> {saving ? "Enregistrement…" : "Comptabiliser"}
        </Button>
      </form>

      <div className="xl:col-span-2 border border-zinc-200 bg-white rounded-md p-6" data-testid="quick-preview">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">Aperçu de l'écriture</div>
        {amount > 0 ? (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-zinc-100">
              <tr><td className="py-2 text-zinc-500">HT</td><td className="py-2 text-right font-medium" data-testid="quick-preview-ht">{fmtN(ht)}</td></tr>
              <tr><td className="py-2 text-zinc-500">TVA ({rate}%)</td><td className="py-2 text-right font-medium" data-testid="quick-preview-tva">{fmtN(tvaAmt)}</td></tr>
              <tr><td className="py-2 text-zinc-500">TTC</td><td className="py-2 text-right font-medium" data-testid="quick-preview-ttc">{fmtN(amount)}</td></tr>
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-zinc-500">Saisissez un montant pour prévisualiser la ventilation HT / TVA.</p>
        )}
        <p className="text-xs text-zinc-400 mt-4">
          Contrepartie automatique : {isNoteFrais ? (beneficiary === "dirigeant" ? "4621 compte courant associé" : "4212 personnel — acomptes") : kind.startsWith("caisse") ? "571 Caisse" : "521 Banque"}.
          TVA {kind.includes("recette") ? "collectée 4431" : isNoteFrais ? "récupérable 4454" : "récupérable 4452"}.
        </p>
      </div>
    </div>
  );
}
